import Vue from 'vue'
import VueRouter from 'vue-router'
import store from "@/store";
// Routes
import { canNavigate } from '@/libs/acl/routeProtection'
import { isUserLoggedIn, getUserData, getHomeRouteForLoggedInUser } from '@/auth/utils'
import software from './routes/software'
import company from './routes/company'
import dashboard from './routes/dashboard'
import reports from './routes/reports'
import pages from './routes/pages'
import components from './routes/components'
import model from './routes/model'
import scenario from './routes/scenario'
import dimensions from './routes/dimensions'
import report from './routes/report'
import dataconnector from './routes/dataconnector'
import workflow from './routes/workflow'
import admin from './routes/admin'
import audit from './routes/audit'

import { rulesFor } from '@/@core/libs/acl/roles'
import ability from '@/libs/acl/ability'

Vue.use(VueRouter)
import useJwt from '@/auth/jwt/useJwt'

// Suppress benign navigation promise rejections when guards redirect
// (e.g., user lacks permission and is redirected by beforeEach)
const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location, onResolve, onReject) {
    if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject)
    return originalPush.call(this, location).catch(err => err)
}

const originalReplace = VueRouter.prototype.replace
VueRouter.prototype.replace = function replace(location, onResolve, onReject) {
    if (onResolve || onReject) return originalReplace.call(this, location, onResolve, onReject)
    return originalReplace.call(this, location).catch(err => err)
}

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    scrollBehavior() {
        return { x: 0, y: 0 }
    },
    routes: [
        { path: '/', redirect: { name: 'dashboard' } },
        ...software,
        ...model,
        ...scenario,
        ...dimensions,
        ...company,
        ...dashboard,
        ...pages,
        ...report,
        ...reports,
        ...dataconnector,
        ...components,
        ...workflow,
        ...admin,
        ...audit,
        {
            path: '*',
            redirect: 'error-404',
        },
    ],
})

router.beforeEach((to, _, next) => {
    const isLoggedIn = isUserLoggedIn()
    const pagesWithoutLogin = [
        "auth-login", 
        "auth-forgot-password", 
        "auth-reset-password-v1",
        "company-signup",
        "auth-register",
        "validate-company-token"
    ]

    
    try {
        ability.update(rulesFor(store.state.workspace.company.group_name))
        if (!canNavigate(to)) {
            // Redirect to login if not logged in
            if (!isLoggedIn) {
                console.log("not logged in")
                localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
                localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

                // Remove userData from localStorage
                localStorage.removeItem('userData')

                // Avoid redirect loop and replace history entry
                if (to && to.name !== 'auth-login') return next({ name: 'auth-login', replace: true })
                return next()
            }

            // If logged in => not authorized
            if (to && to.name !== 'not-authorized') return next({ name: 'not-authorized', replace: true })
            return next()
        }
    } catch (error) {
        console.error('[Router] beforeEach error:', error)
        if (to && to.name !== 'not-authorized') return next({ name: 'not-authorized', replace: true })
        return next()
    }

    if (!isLoggedIn && !pagesWithoutLogin.includes(to.name)) {

        localStorage.removeItem(useJwt.jwtConfig.storageTokenKeyName)
        localStorage.removeItem(useJwt.jwtConfig.storageRefreshTokenKeyName)

        // Remove userData from localStorage
        localStorage.removeItem('userData')
        if (to && to.name !== 'auth-login') return next({ name: 'auth-login', replace: true })
        return next()
    }

    // Redirect if logged in
    if (to.meta.redirectIfLoggedIn && isLoggedIn) {
        const userData = getUserData()

        store.dispatch('notifications/update');

        return next(getHomeRouteForLoggedInUser(userData ? userData.role : null))
    }

    
    return next()
})

// ? For splash screen
// Remove afterEach hook if you are not using splash screen
router.afterEach(() => {
    // Remove initial loading
    const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
        appLoading.style.display = 'none'
    }
})

export default router
